import { useState, useEffect } from 'react';
import type { UserRole } from '@/context/AuthContext';
import { useActiveMunicipio } from '@/context/ActiveMunicipioContext';
import { useModalAnimation } from '@/hooks/useModalAnimation';
import { supabase } from '@/lib/supabase';

interface Municipio {
  id: string;
  nome: string;
  sigla: string | null;
}

interface UserFormModalProps {
  mode: 'create' | 'edit';
  isSuperAdmin?: boolean;
  initialData?: {
    id?: string;
    email?: string;
    full_name?: string;
    role?: UserRole;
    municipio_id?: string | null;
  };
  onClose: () => void;
  onSave: (data: { email: string; full_name: string; role: UserRole; password?: string; municipio_id?: string }) => Promise<void>;
}

const roleLabels: Record<UserRole, string> = {
  super_admin: 'Super Admin',
  admin: 'Administrador',
  viewer: 'Visualizador',
};

const roleDescriptions: Record<UserRole, string> = {
  super_admin: 'Acesso total, incluindo gestão de utilizadores e municípios',
  admin: 'Pode editar configurações e dados do seu município',
  viewer: 'Apenas visualização, sem edição',
};

export default function UserFormModal({ mode, isSuperAdmin, initialData, onClose, onSave }: UserFormModalProps) {
  const { animating, handleClose } = useModalAnimation(onClose);
  const { activeMunicipio } = useActiveMunicipio();
  const [email, setEmail] = useState(initialData?.email ?? '');
  const [fullName, setFullName] = useState(initialData?.full_name ?? '');
  const [role, setRole] = useState<UserRole>(initialData?.role ?? 'viewer');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [municipios, setMunicipios] = useState<Municipio[]>([]);
  // Pré-selecionar: se edição usa o do utilizador; se criação usa o módulo ativo
  const defaultMunicipioId = initialData?.municipio_id
    ?? activeMunicipio?.id
    ?? '00000000-0000-0000-0000-000000000001';
  const [municipioId, setMunicipioId] = useState<string>(defaultMunicipioId);

  useEffect(() => {
    if (isSuperAdmin) {
      supabase.from('municipios').select('id, nome, sigla').eq('ativo', true).order('nome').then(({ data }) => {
        setMunicipios(data ?? []);
      });
    }
  }, [isSuperAdmin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (mode === 'create' && password.length < 6) {
      setError('A password deve ter pelo menos 6 caracteres.');
      return;
    }
    if (mode === 'create' && password !== confirmPassword) {
      setError('As passwords não coincidem.');
      return;
    }

    setLoading(true);
    try {
      await onSave({
        email,
        full_name: fullName,
        role,
        password: password || undefined,
        ...(isSuperAdmin ? { municipio_id: municipioId } : {}),
      });
      // A página pai é responsável por fechar o modal após fetchUsers
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao guardar utilizador.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <h2 className="text-base font-semibold text-slate-800">
            {mode === 'create' ? 'Novo Utilizador' : 'Editar Utilizador'}
          </h2>
          <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer rounded-lg hover:bg-slate-100 transition-colors">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Nome */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Nome completo</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="João Silva"
              required
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-400 transition-colors"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nome@camara.pt"
              required
              disabled={mode === 'edit'}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-400 transition-colors disabled:bg-slate-50 disabled:text-slate-400"
            />
            {mode === 'edit' && (
              <p className="text-xs text-slate-400 mt-1">O email não pode ser alterado.</p>
            )}
          </div>

          {/* Município — só super_admin vê */}
          {isSuperAdmin && municipios.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                <i className="ri-building-4-line text-xs mr-1 text-slate-400" />
                Módulo / Município
              </label>
              <select
                value={municipioId}
                onChange={(e) => setMunicipioId(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-400 transition-colors bg-white cursor-pointer"
              >
                {municipios.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nome}{m.sigla ? ` (${m.sigla})` : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-400 mt-1">
                {mode === 'create' && activeMunicipio?.id === municipioId
                  ? <><i className="ri-checkbox-circle-line text-emerald-500 mr-1" />Pré-selecionado com base no módulo ativo.</>
                  : 'O utilizador só verá os dados deste módulo.'
                }
              </p>
            </div>
          )}

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Permissão</label>
            <div className="space-y-2">
              {(Object.keys(roleLabels) as UserRole[]).map((r) => (
                <label key={r} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${role === r ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`}>
                  <input
                    type="radio"
                    name="role"
                    value={r}
                    checked={role === r}
                    onChange={() => setRole(r)}
                    className="mt-0.5 cursor-pointer"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-800">{roleLabels[r]}</p>
                    <p className="text-xs text-slate-500">{roleDescriptions[r]}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Password */}
          {mode === 'create' && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    required
                    className="w-full pr-10 px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-400 transition-colors"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer">
                    <i className={showPassword ? 'ri-eye-off-line text-sm' : 'ri-eye-line text-sm'} />
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirmar Password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repetir password"
                  required
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-400 transition-colors"
                />
              </div>
            </>
          )}

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-red-500 text-sm" />
              </div>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={handleClose} className="flex-1 py-2.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap">
              Cancelar
            </button>
            <button type="submit" disabled={loading} className="flex-1 py-2.5 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap">
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />
                  A guardar...
                </span>
              ) : (
                mode === 'create' ? 'Criar Utilizador' : 'Guardar Alterações'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
